import React from 'react';
import { Link } from 'react-router-dom';

/*
 * /about — rebuilt 2 Sep 2026 on the landing-page baseline.
 *
 * The previous version described a matching service between individuals and
 * "professionals", which is the model the site has moved away from, and it
 * carried the science story that now lives on /blog/the-research.
 *
 * What an About page is for here: who we are, what we are trying to do, and the
 * statutory disclosure. The evidence claims are not restated — they have a page
 * of their own, and repeating them in two places is how they drift apart.
 *
 * The trading disclosure at the bottom is a legal requirement, not decoration.
 * Companies Act 2006 s.82 and the Names and Trading Disclosures Regulations
 * 2015 require registered name, number and place of registration on a company's
 * website. Do not remove it without Legal.
 */

const AboutPage = () => (
    <div className="pa2">

        <section className="hero" id="top">
            <div className="wrap">
                <h1 className="on-dark">
                    <span className="setup">Physiotherapy for people</span>
                    <span className="payoff">who are not in a queue.</span>
                </h1>
                <p className="lede">
                    Musculoskeletal Health, MSK Health, is one of the biggest causes of lost working
                    days in the UK, and almost all of the support for it starts after something has
                    already gone wrong. We measure movement continuously instead, so a
                    physiotherapist can work from what is actually happening.
                </p>
            </div>
        </section>

        <section className="tint" id="what">
            <div className="wrap">
                <h2><span className="setup">What we do.</span></h2>
                <p className="lede">
                    We read how movement loads the body, from sensors people already carry. A
                    registered physiotherapist builds a programme from it, and the programme
                    changes as the person does. Employers provide it to everyone they cover; the
                    person keeps it.
                </p>

                <div className="cards">
                    <div className="card">
                        <h3>Measured, not reported</h3>
                        <p>No questionnaires, no logging, no relying on what someone remembers at their next appointment.</p>
                    </div>
                    <div className="card">
                        <h3>Everyone, not the queue</h3>
                        <p>Coverage is provisioned rather than claimed, so it reaches people who would never have asked.</p>
                    </div>
                    <div className="card">
                        <h3>A named clinician</h3>
                        <p>Software proposes; an HCPC-registered physiotherapist decides. That has not been automated and will not be.</p>
                    </div>
                </div>

                <p className="quiet">
                    <Link to="/employers">For employers</Link> ·{' '}
                    <Link to="/individuals">For individuals</Link> ·{' '}
                    <Link to="/physiotherapists">Partner with us</Link>
                </p>
            </div>
        </section>

        <section className="white" id="claims">
            <div className="wrap">
                <h2>
                    <span className="setup">What we will not say.</span>
                </h2>
                <p className="lede">
                    We are a young company in a field where overclaiming is normal. The measurement
                    came out of academic research at the University of Southampton, and that is a
                    statement about where it came from, not evidence that it changes outcomes.
                </p>
                <p className="lede">
                    So we do not claim reduced absence, prevented injury or improved clinical
                    outcomes. We are not a medical device. Where a claim is not evidenced, we do
                    not make it, and we would rather be the vendor that says so.
                </p>
                <p className="quiet">
                    <Link to="/blog/the-research">The research behind this</Link> sets out each
                    claim and its limits in full.
                </p>
            </div>
        </section>

        <section className="s-promise" id="contact">
            <div className="wrap">
                <h2 className="on-dark">
                    <span className="setup">Talk to us.</span>
                </h2>
                <p className="lede">
                    Whether you are looking at this for an organisation or for yourself, the same
                    form reaches us and we reply to every enquiry.
                </p>
                <div className="cta-row">
                    <Link className="btn btn-t" to="/contact">Contact us</Link>
                </div>
            </div>
        </section>

        {/* Statutory trading disclosure. UK companies must show registered name,
            registered number and place of registration on their website —
            Companies Act 2006 s.82 and the Names and Trading Disclosures
            Regulations 2015. The requirement is that it appears on the website,
            not on every page, so it lives here. Do not delete without Legal. */}
        <section className="white" id="company">
            <div className="wrap">
                <p className="quiet">
                    PHYSIOACTIVITY LTD. Registered in England and Wales, company number 15740886.
                </p>
            </div>
        </section>

    </div>
);

export default AboutPage;
